import type {ListGroup} from 'src/design/molecules/GroupedList';
import {parseUtcDate} from 'src/shared/utils/parseUtcDate';
import {padTwo as pad, startOfUtcWeek} from 'src/shared/utils/utcDate';

export type TimelineMode = 'day' | 'week' | 'month';

export const TIMELINE_MODE_OPTIONS = [
  {label: 'Day', value: 'day'},
  {label: 'Week', value: 'week'},
  {label: 'Month', value: 'month'},
];

const UNSCHEDULED_KEY = 'unscheduled';

const bucketKey = (date: Date, mode: TimelineMode): string => {
  switch (mode) {
    case 'day':
      return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
    case 'week': {
      const start = startOfUtcWeek(date);
      return `week-${start.getUTCFullYear()}-${pad(start.getUTCMonth() + 1)}-${pad(start.getUTCDate())}`;
    }
    case 'month':
      return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}`;
  }
};

const bucketLabel = (date: Date, mode: TimelineMode): string => {
  const dayMonthYear = date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
  switch (mode) {
    case 'day':
      return dayMonthYear;
    case 'week':
      return `Week of ${startOfUtcWeek(date).toLocaleDateString(undefined, {month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC'})}`;
    case 'month':
      return date.toLocaleDateString(undefined, {
        month: 'long',
        year: 'numeric',
        timeZone: 'UTC',
      });
  }
};

/**
 * Buckets items by scheduled date into Day/Week/Month groups; null-date items
 * form a single trailing "Unscheduled" group. Empty groups are elided.
 * @registry Groups timeline rows by day/week/month over a server-sorted date key.
 */
export function groupByTimeline<T>(
  items: T[],
  mode: TimelineMode,
  getDate: (item: T) => Date | string | undefined
): ListGroup<T>[] {
  const groups = new Map<string, ListGroup<T>>();
  let unscheduled: ListGroup<T> | undefined;

  for (const item of items) {
    const raw = getDate(item);
    if (!raw) {
      unscheduled ??= {key: UNSCHEDULED_KEY, label: 'Unscheduled', items: []};
      unscheduled.items.push(item);
      continue;
    }
    const date = parseUtcDate(raw);
    const key = bucketKey(date, mode);
    let group = groups.get(key);
    if (!group) {
      group = {key, label: bucketLabel(date, mode), items: []};
      groups.set(key, group);
    }
    group.items.push(item);
  }

  const result = [...groups.values()];
  if (unscheduled) result.push(unscheduled);
  return result;
}
